import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  MenuItem,
  Button,
  Divider,
  Card,
  CardContent,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { Add, Delete, Assignment } from "@mui/icons-material"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearEncuesta = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // ESTADO MAESTRO DE LA ENCUESTA (Cabecera + Detalle)
  const [formData, setFormData] = useState({
    enctitulo: "",
    encdescri: "",
    enctipo: "RAP",
    zoncodigo: "",
    encpublica: 0,
    encstatus: "A",
    preguntas: [
      {
        id: Date.now(), // ID temporal para el renderizado de React
        pregtipo: "U", // U: Libre, L: Lista Cerrada, M: Múltiple
        pregtexto: "",
        pregponderacion: 1.0,
        pregobligatoria: 1,
        pregstatus: "A",
        opciones: [],
      },
    ],
  })

  // MUTACIÓN PARA GUARDAR
  const {
    mutateAsync: SaveCreacionEncuesta,
    isPending: isSaving,
    isError,
  } = useMutation({
    queryKey: ["isCreatingEncuesta"],
    fn: async (payload) => {
      // ⭐ SOLUCIÓN FINAL: Retornamos el objeto Axios completo
      // Así el interceptor global de SIAC puede hacer 'response.data.success' y lanzar el Toast
      return await api.post("/Encuestas/saveEncuesta", payload)
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // ================= LÓGICA DE LA CABECERA =================
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // ================= LÓGICA DE PREGUNTAS =================
  const handleAddPregunta = () => {
    setFormData((prev) => ({
      ...prev,
      preguntas: [
        ...prev.preguntas,
        {
          id: Date.now(),
          pregtipo: "U",
          pregtexto: "",
          pregponderacion: 1.0,
          pregobligatoria: 1,
          pregstatus: "A",
          opciones: [],
        },
      ],
    }))
  }

  const handleRemovePregunta = (idToRemove) => {
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.filter((p) => p.id !== idToRemove),
    }))
  }

  const handleChangePregunta = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.map((p) => {
        if (p.id === id) {
          const updatedPregunta = { ...p, [field]: value }
          // Si cambia a tipo "Libre", limpiamos las opciones
          if (field === "pregtipo" && value === "U") {
            updatedPregunta.opciones = []
          }
          return updatedPregunta
        }
        return p
      }),
    }))
  }

  // ================= LÓGICA DE OPCIONES =================
  const handleAddOpcion = (preguntaId) => {
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.map((p) => {
        if (p.id === preguntaId) {
          return {
            ...p,
            opciones: [...p.opciones, { id: Date.now(), opctexto: "", opcpuntaje: 0.0, opcstatus: "A" }],
          }
        }
        return p
      }),
    }))
  }

  const handleRemoveOpcion = (preguntaId, opcionIdToRemove) => {
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.map((p) => {
        if (p.id === preguntaId) {
          return { ...p, opciones: p.opciones.filter((o) => o.id !== opcionIdToRemove) }
        }
        return p
      }),
    }))
  }

  const handleChangeOpcion = (preguntaId, opcionId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.map((p) => {
        if (p.id === preguntaId) {
          return {
            ...p,
            opciones: p.opciones.map((o) => (o.id === opcionId ? { ...o, [field]: value } : o)),
          }
        }
        return p
      }),
    }))
  }

  // ================= VALIDACIÓN Y GUARDADO =================
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones de Cabecera
    if (!formData.enctitulo.trim()) return showWarning("El título de la encuesta es obligatorio.")
    if (formData.preguntas.length === 0) return showWarning("Debe agregar al menos una pregunta a la encuesta.")

    // Validaciones de Detalle
    for (let i = 0; i < formData.preguntas.length; i++) {
      const p = formData.preguntas[i]
      if (!p.pregtexto.trim()) return showWarning(`La pregunta #${i + 1} no tiene texto.`)

      if (p.pregtipo === "L" || p.pregtipo === "M") {
        if (p.opciones.length === 0) return showWarning(`La pregunta #${i + 1} requiere al menos una opción.`)
        for (let j = 0; j < p.opciones.length; j++) {
          if (!p.opciones[j].opctexto.trim()) return showWarning(`La pregunta #${i + 1} tiene una opción sin texto.`)
        }
      }
    }

    // Si todo es válido, disparamos la mutación
    await SaveCreacionEncuesta(formData)
  }

  // ================= BARRA DE ACCIONES (ESTÁNDAR SIAC) =================
  const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GRABAR" || action?.acccaption === "EJECUTAR",
  )
  const toolbarActions = ejecutarAction
    ? [
        {
          label: ejecutarAction.acccaption,
          key: ejecutarAction.acccaption,
          icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
        },
      ]
    : []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* TOOLBAR */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "white" }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 20px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <Assignment sx={{ fontSize: 32, mr: 1 }} />
          <b>Crear Nueva Encuesta Dinámica</b>
        </div>

        <CustomBackdrop isLoading={isSaving} />

        <Box sx={StyledRoot} component="form" onSubmit={handleSubmit}>
          {/* BLOQUE 1: CABECERA */}
          <Paper
            elevation={2}
            sx={{ p: 4, mb: 4, borderRadius: 3, borderTop: "5px solid #196C87", background: "white" }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              Datos Generales de la Encuesta
            </Typography>
            <Grid container spacing={3} mt={1}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Título de la Encuesta *"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.enctitulo}
                  onChange={(e) => handleInputChange("enctitulo", e.target.value)}
                  error={isError && !formData.enctitulo}
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de Encuesta *"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.enctipo}
                  onChange={(e) => handleInputChange("enctipo", e.target.value)}
                >
                  <MenuItem value="ENL">En línea</MenuItem>
                  <MenuItem value="TEL">Telefónicas</MenuItem>
                  <MenuItem value="PRE">Presenciales</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción u Objetivos (Opcional)"
                  size="small"
                  multiline
                  rows={2}
                  InputLabelProps={{ shrink: true }}
                  value={formData.encdescri}
                  onChange={(e) => handleInputChange("encdescri", e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Audiencia Objetivo *"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.encpublica}
                  onChange={(e) => handleInputChange("encpublica", e.target.value)}
                >
                  <MenuItem value={0}>Lista Específica (Clientes/Usuarios)</MenuItem>
                  <MenuItem value={1}>Pública (Cualquier persona)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Zona de Aplicación (Código)"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  placeholder="Ej: Z01"
                  value={formData.zoncodigo}
                  onChange={(e) => handleInputChange("zoncodigo", e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 3 }}
                  helperText="Deje en blanco si aplica a nivel nacional"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.encstatus}
                  onChange={(e) => handleInputChange("encstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVA</MenuItem>
                  <MenuItem value="I">INACTIVA</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          {/* BLOQUE 2: CONSTRUCTOR DE PREGUNTAS */}
          <Typography
            variant="h6"
            color="primary"
            fontWeight="bold"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            Constructor de Preguntas
          </Typography>

          {formData.preguntas.map((preg, index) => (
            <Card key={preg.id} elevation={1} sx={{ mb: 3, borderLeft: "4px solid #f57c00", overflow: "visible" }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Pregunta #{index + 1}
                  </Typography>
                  <IconButton color="error" size="small" onClick={() => handleRemovePregunta(preg.id)}>
                    <Delete />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Texto de la Pregunta *"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={preg.pregtexto}
                      onChange={(e) => handleChangePregunta(preg.id, "pregtexto", e.target.value)}
                      error={isError && !preg.pregtexto}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Tipo de Respuesta *"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={preg.pregtipo}
                      onChange={(e) => handleChangePregunta(preg.id, "pregtipo", e.target.value)}
                    >
                      <MenuItem value="U">Texto Libre (Abierta)</MenuItem>
                      <MenuItem value="L">Lista Desplegable (Cerrada)</MenuItem>
                      <MenuItem value="M">Casillas Múltiples</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Ponderación (Peso) *"
                      size="small"
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      value={preg.pregponderacion}
                      onChange={(e) =>
                        handleChangePregunta(preg.id, "pregponderacion", parseFloat(e.target.value) || 0)
                      }
                      inputProps={{ step: "0.1", min: "0" }}
                      helperText="Puntaje multiplicador de esta pregunta"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Obligatoria *"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={preg.pregobligatoria}
                      onChange={(e) => handleChangePregunta(preg.id, "pregobligatoria", e.target.value)}
                    >
                      <MenuItem value={1}>SI</MenuItem>
                      <MenuItem value={0}>NO</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                {/* SUB-SECCIÓN: OPCIONES (Solo si es Lista o Múltiple) */}
                {(preg.pregtipo === "L" || preg.pregtipo === "M") && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: "#fdfdfd", border: "1px dashed #ccc", borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="primary" gutterBottom display="block">
                      Opciones de Respuesta
                    </Typography>

                    {preg.opciones.map((opc, opcIndex) => (
                      <Grid container spacing={2} alignItems="center" key={opc.id} sx={{ mb: 1 }}>
                        <Grid item xs={12} sm={7}>
                          <TextField
                            fullWidth
                            placeholder={`Opción ${opcIndex + 1}`}
                            size="small"
                            value={opc.opctexto}
                            onChange={(e) => handleChangeOpcion(preg.id, opc.id, "opctexto", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={9} sm={4}>
                          <TextField
                            fullWidth
                            label="Valor/Puntaje"
                            size="small"
                            type="number"
                            value={opc.opcpuntaje}
                            onChange={(e) =>
                              handleChangeOpcion(preg.id, opc.id, "opcpuntaje", parseFloat(e.target.value) || 0)
                            }
                            inputProps={{ step: "0.1" }}
                          />
                        </Grid>
                        <Grid item xs={3} sm={1}>
                          <IconButton color="error" size="small" onClick={() => handleRemoveOpcion(preg.id, opc.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<Add />}
                      size="small"
                      color="primary"
                      onClick={() => handleAddOpcion(preg.id)}
                      sx={{ mt: 1 }}
                    >
                      Añadir Opción
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}

          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={handleAddPregunta}
            sx={{ mb: 4, py: 1 }}
          >
            Agregar Nueva Pregunta
          </Button>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearEncuesta
